
import { useForm, FormProvider, Controller, UseFormReturn } from "react-hook-form";
import { useCallback, useContext, useState } from "react";
import { RegisterContext } from "@profile/contexts/RegisterContext";
import { TextField, Button, SSNField } from "@fmc-digital/ui";
import { FreedomLoanNumberModal } from "@profile/components/atoms/FreedomLoanNumberModal";

interface PersonalInfoFormValues {
  lastName: string;
  loanNumber: string;
  ssn: string;
}


interface PersonalInfoStepProps {
  setStatusMessage: (msg: string | null) => void;
}

function validateLoanNumber(value: string): true | string {
  if (!value) return "You must enter the 'Loan Number' associated with your loan to continue.";
  // Only alphanumeric and at most one hyphen
  if (!/^[A-Za-z0-9-]+$/.test(value)) return "Loan Number can only contain letters, numbers, and at most one hyphen.";
  const hyphenCount = (value.match(/-/g) || []).length;
  if (hyphenCount > 1) return "Loan Number can have at most one hyphen.";
  const len = value.length;
  const hyphenIdx = value.indexOf('-');
  // Hyphen rules
  if (hyphenCount === 1) {
    if (hyphenIdx === 0 || hyphenIdx === len - 1) return "Hyphen cannot be at the start or end of the Loan Number.";
    if (len < 10) return "Loan Number with hyphen must be at least 10 characters.";
    if (len === 10 && hyphenIdx !== 7) return "For 10-character Loan Numbers, hyphen must be at position 7.";
    if ((len === 11 || len === 12) && hyphenIdx !== len - 3) return "For 11-12 character Loan Numbers, hyphen must be at position (length - 3).";
    if (len === 13 && hyphenIdx !== 10) return "For 13-character Loan Numbers, hyphen must be at position 10.";
    // Must have exactly 2 digits after hyphen
    const afterHyphen = value.slice(hyphenIdx + 1);
    if (!/^\d{2}$/.test(afterHyphen)) return "There must be exactly 2 digits after the hyphen in the Loan Number.";
    // At least 1 alphanumeric before hyphen
    if (!/[A-Za-z0-9]/.test(value.slice(0, hyphenIdx))) return "There must be at least one alphanumeric character before the hyphen.";
  } else {
    if (len < 10) return true; // valid, will be padded
    if (len === 10) return true;
    if (len > 10) return "Loan Number without hyphen must be exactly 10 characters or less.";
  }
  return true;
}

function validateSSN(value: string): true | string {
  if (!value) return "You must enter the 'Social Security' Number associated with your loan to continue.";
  if (!/^\d{9}$/.test(value)) return "SSN must be exactly 9 digits (no dashes).";
  return true;
}

function normalizeAccountNumber(value: string): string {
  const trimmed = value.trim();
  if (!trimmed.includes("-") && trimmed.length < 10) {
    return trimmed.padStart(10, "0");
  }
  return trimmed;
}

export function PersonalInfoStep({ setStatusMessage }: PersonalInfoStepProps) {
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const handleOpenHelpModal = useCallback(() => setHelpModalOpen(true), []);
  const handleCloseHelpModal = useCallback(() => setHelpModalOpen(false), []);
  const handleVisitNewTransfer = useCallback(() => {
    setHelpModalOpen(false);
    window.open("https://www.freedommortgage.com/newtransfer", "_blank", "noopener,noreferrer");
  }, []);
  const { personalInfo, setPersonalInfo, goNext } = useContext(RegisterContext);

  const form = useForm<PersonalInfoFormValues>({
    defaultValues: {
      lastName: personalInfo.lastName || "",
      loanNumber: personalInfo.loanNumber || "",
      ssn: personalInfo.ssn || "",
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const { handleSubmit } = form;

  const onSubmit = (data: PersonalInfoFormValues) => {
    if (setStatusMessage) setStatusMessage(null);
    // Only update fields that exist in personalInfo
    setPersonalInfo({
      ...personalInfo,
      lastName: data.lastName,
      ...(personalInfo.hasOwnProperty('loanNumber') ? { loanNumber: normalizeAccountNumber(data.loanNumber) } : {}),
      ...(personalInfo.hasOwnProperty('ssn') ? { ssn: data.ssn } : {}),
    });
    goNext();
  };

  return (
    <FormProvider {...form}>
      <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="lastName"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "You must enter the 'Last Name' associated with your loan to continue.",
            },
          }}
          render={({ field, fieldState }) => (
            <TextField
              label="Last name"
              value={field.value ?? ""}
              onChange={field.onChange}
              onBlur={field.onBlur}
              fullWidth
              error={fieldState.error?.message}
            />
          )}
        />
        <Controller
          name="loanNumber"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "You must enter the 'Loan Number' associated with your loan to continue.",
            },
            validate: validateLoanNumber,
          }}
          render={({ field }) => (
            <div className="flex flex-col gap-1">
              <TextField
                id="loanNumber"
                label="Loan number"
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                maxLength={13}
                fullWidth
                error={form.formState.errors.loanNumber?.message}
              />
              <p
                style={{
                  paddingLeft: 16,
                  paddingRight: 16,
                  color: 'var(--Schemes-On-Surface-Variant, #44474C)',
                  fontFamily: 'Poppins, sans-serif',
                  fontSize: '12px',
                  fontStyle: 'normal',
                  fontWeight: 400,
                  lineHeight: '20px',
                  letterSpacing: '0.25px',
                  margin: 0,
                }}
              >
                You received this{' '}
                <button
                  type="button"
                  style={{
                    textDecoration: 'underline',
                    color: 'var(--Primary-600, #005CB9)',
                    fontFamily: 'Poppins, sans-serif',
                    fontSize: '12px',
                    fontStyle: 'normal',
                    fontWeight: 500,
                    lineHeight: '20px',
                    letterSpacing: '0.25px',
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    margin: 0,
                    cursor: 'pointer',
                  }}
                  onClick={handleOpenHelpModal}
                  onMouseOver={e => (e.currentTarget.style.color = 'var(--Primary-800, #003A6D)')}
                  onMouseOut={e => (e.currentTarget.style.color = 'var(--Primary-600, #005CB9)')}
                  onFocus={e => (e.currentTarget.style.color = 'var(--Primary-800, #003A6D)')}
                  onBlur={e => (e.currentTarget.style.color = 'var(--Primary-600, #005CB9)')}
                >
                  loan number
                </button>{' '}
                in your welcome packet we mailed to you.
              </p>
            </div>
          )}
        />
        <Controller
          name="ssn"
          control={form.control}
          rules={{
            required: {
              value: true,
              message: "You must enter the 'Social Security' Number associated with your loan to continue.",
            },
            validate: validateSSN,
          }}
          render={({ field }) => {
            // Remove ref, only pass supported props
            const { ref, ...fieldProps } = field;
            return (
              <SSNField
                {...fieldProps}
                form={form as unknown as UseFormReturn}
                name="ssn"
                showBootstrap={true}
              />
            );
          }}
        />
        <Button
          variant="filled"
          fullWidth
          type="submit"
          disabled={!form.formState.isValid}
        >
          Next
        </Button>
      </form>
      <FreedomLoanNumberModal
        open={helpModalOpen}
        onClose={handleCloseHelpModal}
        onVisitNewTransfer={handleVisitNewTransfer}
      />
    </FormProvider>
  );
}
